import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import { ListingProps } from "types/listing";
import { getEnvVariable } from "utilities/env";

export type VariableGroupType = {
  groupOfValuesId: string;
  name: string;
  description?: string;
  active: boolean;
  recordsInGroup: number;
  updatedAt: string;
  createdAt: string;
};

export type VariableGroupList = VariableGroupType[];

export const useVariableGroups = () => {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher, arrayFetcher } = useAuth();

  const useListOfVariableGroups = (props?: ListingProps) => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/groupOfValues${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useInfiniteListOfVariableGroups = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<VariableGroupType>>(
      (index) =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/groupOfValues${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const useVariableGroupDetails = (groupId?: string | string[]) => {
    const isSingleRequest = groupId && !Array.isArray(groupId);

    return useSWR(
      () =>
        code && groupId
          ? isSingleRequest
            ? `${getEnvVariable(
                "REACT_APP_API_URL",
              )}/${code}/groupOfValues/${groupId}`
            : (groupId as string[]).map(
                (id) =>
                  `${getEnvVariable(
                    "REACT_APP_API_URL",
                  )}/${code}/groupOfValues/${id}`,
              )
          : null,
      isSingleRequest ? fetcher : arrayFetcher,
    );
  };

  return {
    useListOfVariableGroups,
    useVariableGroupDetails,
    useInfiniteListOfVariableGroups,
  };
};
